import { Color, Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, { TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import { Importer } from 'dashboard/models/importer';
import {
  ErrorDetail,
  GlobalError,
  IMPORTER_RUN_GLOBAL_ERROR_TYPE_TO_NAME,
  IMPORTER_RUN_STATUS_TO_LABEL,
  IMPORTER_RUN_STATUS_TO_VARIANT,
  ImporterRun,
  ImporterRunStatus,
  RunData,
} from 'dashboard/models/importer-run';
import ImporterRunErrorsTable from 'dashboard/views/DashboardImporterRunDetailsView/ImporterRunErrorsTable';
import ImporterRunSuccessesTable from 'dashboard/views/DashboardImporterRunDetailsView/ImporterRunSuccessesTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { paragraphMixins } from 'design-system/mixins/text';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';

export interface EditImporterData {
  dataSourceId: string;
  destination: 'Account' | 'Txn';
  destinationTypeId: string;
  sourceDestColumnMapping: { [key: string]: string };
  tableName: string;
}

interface Processed {
  id: string;
  attributes: {
    'started-at': string;
    'ended-at': string;
    status: string;
  };
}

export interface DashboardImporterRunDetailsViewProps {
  cancelImporterRun: () => Promise<void>;
  dataSource: ExternalIntegrationDataSource;
  handleDownloadErrors: () => void;
  handleDownloadSuccesses: () => void;
  importer: Importer;
  importerRun: ImporterRun;
  processed?: Processed[]; // todo: create Processed type and this will be Processed[]
}

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
`;

const StatusWrapper = styled.div`
  display: flex;
  height: 22px;
`;

const HeaderTable = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[4]};
  font-size: 14px;
`;

const SubHeader = styled.div`
  ${paragraphMixins.lgBold};
`;

const HeaderColumn = styled.div`
  flex: 2 1 auto;
`;

const HeaderLabel = styled.div`
  color: ${Color.CoreSlate700};
`;

const StyledTableTR = styled.tr`
  // default background color is white and not transparent
  background-color: ${({ theme }) => theme.color.primary.white};
`;

const DashboardImporterRunDetailsView = ({
  cancelImporterRun,
  dataSource,
  handleDownloadErrors,
  handleDownloadSuccesses,
  importer,
  importerRun,
}: DashboardImporterRunDetailsViewProps) => {
  const [
    cancelImporterConfirmationModalOpen,
    { enable: showCancelConfirmationModal, disable: hideCancelConfirmationModal },
  ] = useToggle(false);
  const runData: RunData = importerRun.attributes['run-data'];
  const errorDetails: ErrorDetail = runData['error-details'];

  const remainingItemCount = useMemo(() => {
    return Math.max(
      runData['estimated-total-count'] - runData['error-count'] - runData['success-count'],
      0,
    );
  }, [runData]);

  return (
    <>
      <ModalWrapper
        isOpen={cancelImporterConfirmationModalOpen}
        handleCloseModal={hideCancelConfirmationModal}
      >
        <ConfirmationModal
          cancelAction={hideCancelConfirmationModal}
          confirmAction={cancelImporterRun}
          header='Cancel Run?'
          confirmActionText='Yes'
          cancelActionText='No'
        >
          This action is permanent and can’t be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <Header
        title=''
        prepend={
          <HeaderTitle>
            <NavBack
              data-test='importer-details-nav-back'
              to={`/dashboard/imports/importers/${importer.id}`}
            />
            Importer Run Details: {importer.attributes.name}
          </HeaderTitle>
        }
      >
        {![
          ImporterRunStatus.Errored,
          ImporterRunStatus.Canceled,
          ImporterRunStatus.Killed,
          ImporterRunStatus.Completed,
        ].includes(importerRun.attributes.status) && (
          <HStack>
            <Button
              width='auto'
              onClick={(e) => {
                e.preventDefault();
                showCancelConfirmationModal();
              }}
            >
              Cancel
            </Button>
          </HStack>
        )}
      </Header>
      <Container>
        <Section>
          <Card>
            <Card.Body>
              <Card.Title>Overview</Card.Title>
              <HeaderTable>
                <HeaderColumn>
                  <HeaderLabel>Data source</HeaderLabel>
                  <div>{dataSource.attributes.name}</div>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Status</HeaderLabel>
                  <StatusWrapper>
                    <Sticker
                      variant={IMPORTER_RUN_STATUS_TO_VARIANT[importerRun.attributes.status]}
                    >
                      {IMPORTER_RUN_STATUS_TO_LABEL[importerRun.attributes.status]}
                    </Sticker>
                  </StatusWrapper>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Destination</HeaderLabel>
                  <div>{importer.attributes['pipeline-config']['destination']}</div>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Started</HeaderLabel>
                  <div>
                    {DateTime.fromISO(importer.attributes['created-at']).toFormat(
                      DATE_AND_TIME_FORMAT,
                    )}
                  </div>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Importer ID</HeaderLabel>
                  <div>{importer.id}</div>
                </HeaderColumn>
              </HeaderTable>
            </Card.Body>
          </Card>
          <Spacer height={1} />
          <Card>
            <Card.Body>
              <Card.Title>Run Details</Card.Title>
              <SubHeader>Overview</SubHeader>
              <HeaderTable>
                <HeaderColumn>
                  <HeaderLabel>Not yet processed:</HeaderLabel>
                  <div>{remainingItemCount == null ? '--' : remainingItemCount}</div>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Create object:</HeaderLabel>
                  <div>{runData['created-count'] == null ? '--' : runData['created-count']}</div>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Update object:</HeaderLabel>
                  <div>{runData['updated-count'] == null ? '--' : runData['updated-count']}</div>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Rows successful:</HeaderLabel>
                  <div>{runData['success-count'] == null ? '--' : runData['success-count']}</div>
                </HeaderColumn>
                <HeaderColumn>
                  <HeaderLabel>Rows Errored:</HeaderLabel>
                  <div>{runData['error-count'] == null ? '--' : runData['error-count']}</div>
                </HeaderColumn>
              </HeaderTable>
              {errorDetails['global-errors'].length > 0 && (
                <>
                  <Spacer height={2} />
                  <Card.Title>{`Global Errors (${errorDetails['global-errors'].length})`}</Card.Title>
                  <TableLayout
                    showHeader
                    containerMaxHeight={500}
                    isEmpty={!errorDetails?.['global-errors'].length}
                    nullState={null}
                    headerTR={
                      <>
                        <TableTH key='global-error'>Error</TableTH>
                      </>
                    }
                  >
                    {errorDetails['global-errors'].map((globalError: GlobalError, i) => {
                      return (
                        <StyledTableTR key={`global-error-${i}`}>
                          <TableTD key={`global-error-${i}-error`}>
                            {`${IMPORTER_RUN_GLOBAL_ERROR_TYPE_TO_NAME[globalError['error-type']]}: ${globalError.message}`}
                          </TableTD>
                        </StyledTableTR>
                      );
                    })}
                  </TableLayout>
                </>
              )}
              <ImporterRunErrorsTable
                importerRun={importerRun}
                handleDownloadCSV={handleDownloadErrors}
              />
              <ImporterRunSuccessesTable
                importerRun={importerRun}
                importer={importer}
                handleDownloadCSV={handleDownloadSuccesses}
              />
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardImporterRunDetailsView;
